import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import Image from "common/blocks/Image";
import MediaPlayer from "common/blocks/MediaPlayer";
import React from "react";
import type { ResolvedImage } from "resolvers/data/imageResolver";
import { ResolvedRichText } from "resolvers/data/richTextResolver";
import type { ResolvedVideo } from "resolvers/data/videoResolver";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";
import RichText from "../RichText";
import styles from "./TextMediaBlock.module.scss";

const cx = classNames.bind(styles);

export interface TextMediaBlockProps {
    title?: string | ResolvedRichText;
    content: React.ReactNode | ResolvedRichText;
    reversedOrder?: boolean;
    buttonHref?: string;
    buttonText?: string;
    media?: ResolvedImage | ResolvedVideo;
    isSmall?: boolean;
    isHero?: boolean;
    isEvent?: boolean;
    subTitle?: string;
}

const TextMediaBlock = (props: TextMediaBlockProps) => {
    const {
        title,
        content,
        reversedOrder = false,
        buttonHref,
        buttonText,
        isSmall = false,
        media,
        isHero = false,
        isEvent = false,
        subTitle,
    } = props;

    const withImage = media?.type === ContentTypeCodenames.Image;
    const withVideo = media?.type === ContentTypeCodenames.Video;
    const titleIsRichText = typeof title !== "string";

    return (
        <section
            className={cx(
                "section",
                { reversedOrder },
                { isHero },
                { isSmall },
                { isVideo: withVideo },
                { isEvent }
            )}
        >
            <div className={cx("grid")}>
                <div className={cx("textColumn")}>
                    {titleIsRichText && !!title && <RichText {...(title as ResolvedRichText)} />}
                    {!titleIsRichText && !!title && (
                        <h2 className={cx("title")}>{title as string}</h2>
                    )}
                    {subTitle && !!subTitle && <h3 className={cx("subTitle")}>{subTitle}</h3>}
                    {!isEvent && content && <RichText {...(content as ResolvedRichText)} />}
                    {isEvent && content && (
                        <div className={cx("textContent")}>{content as React.ReactNode}</div>
                    )}
                    {(isHero || isEvent) && buttonText && (
                        <div className={cx("buttonWrap")}>
                            <Button href={buttonHref} fullWidth={true} className={cx("button")}>
                                {buttonText}
                            </Button>
                        </div>
                    )}
                </div>

                <div className={cx("mediaColumn")}>
                    {withImage && (
                        <div className={cx("mediaWrap")}>
                            {
                                // eslint-disable-next-line jsx-a11y/alt-text
                                <Image {...media} layout="fill" objectFit="cover" quality={75} />
                            }
                        </div>
                    )}
                    {withVideo && (
                        <div className={cx("mediaWrap")}>
                            <MediaPlayer {...media} light />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default TextMediaBlock;
